import { Link, usePage } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";
import styled from "styled-components";

import {
  LandingLanguageProvider,
  useLandingLanguage,
} from "../../../contexts/landing-language";
import { categoryPath, insightPath } from "../../../components/insights/paths";
import ArticleCard from "../../../components/insights/ArticleCard";
import Footer from "../home/Footer";
import Header from "../home/Header";
import SeoHead from "../../../components/insights/SeoHead";

// Verbatim copy of Insights::SeoPresenter#index_title. Inertia's head manager
// replaces the server-rendered <title> on hydration, so anything else here
// would show the reader a title the crawler never indexed. Keep both in sync.
const SEO_TITLE = {
  en: "HEBE Insights — Stories & Local Guides in Bogor",
  id: "HEBE Insights — Cerita & Panduan Lokal Bogor",
};

const Page = styled.div`
  background: #faf9f7;
  min-height: 100vh;
`;

const Hero = styled.section`
  background: linear-gradient(180deg, #1a1a1a 0%, #23201b 100%);
  color: #fff;
  padding: 160px 24px 80px;
  text-align: center;
`;

const HeroInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
`;

const Kicker = styled.span`
  display: inline-block;
  font-size: 12px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #c9a961;
  margin-bottom: 18px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(34px, 5vw, 54px);
  line-height: 1.1;
  font-weight: 800;
  margin: 0 0 18px;
`;

const HeroSub = styled.p`
  font-size: clamp(15px, 2vw, 18px);
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.72);
  margin: 0;
`;

// Match the marketing Header's inner box (max-width 1400, 40px side padding) so
// the outermost content lines up vertically with the logo (left) and actions (right).
const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const Section = styled.section`
  padding: 64px 0;

  &:last-of-type {
    padding-bottom: 96px;
  }
`;

const SectionHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(22px, 3vw, 30px);
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
`;

const SectionDesc = styled.p`
  font-size: 14.5px;
  line-height: 1.6;
  color: #5e6278;
  margin: 6px 0 0;
  max-width: 620px;
`;

const ViewAll = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #c9a961;
  }
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: 1.35fr 1fr;
  gap: 28px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LeadCard = styled(Link)`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  min-height: 480px;
  display: flex;
  align-items: flex-end;
  text-decoration: none;
  color: #fff;
  background-image:
    linear-gradient(180deg, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0.85) 100%),
    url(${(props) => props.$src});
  background-size: cover;
  background-position: center;
  transition: transform 0.4s ease;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 900px) {
    min-height: 380px;
  }
`;

const LeadBody = styled.div`
  padding: 36px;
`;

const LeadChip = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #23201b;
  background: #c9a961;
  padding: 6px 12px;
  border-radius: 999px;
  margin-bottom: 14px;
`;

const LeadTitle = styled.h3`
  font-size: clamp(22px, 2.6vw, 30px);
  line-height: 1.2;
  font-weight: 800;
  margin: 0 0 10px;
`;

const LeadExcerpt = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  max-width: 520px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Compact list beside the lead — its four rows distribute to match the lead's
// height, so the block reads as one balanced unit instead of one very tall card.
const SideList = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const SideItem = styled(Link)`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 16px;
  align-items: center;
  padding: 14px 0;
  text-decoration: none;
  color: inherit;
  border-bottom: 1px solid #ececec;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:hover h4 {
    color: #b8933f;
  }
`;

const SideThumb = styled.div`
  width: 120px;
  height: 80px;
  border-radius: 10px;
  background-image: url(${(props) => props.$src});
  background-size: cover;
  background-position: center;
  background-color: #f0ede8;
`;

const SideText = styled.div`
  min-width: 0;
`;

const SideCat = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #c9a961;
`;

const SideTitle = styled.h4`
  font-size: 15.5px;
  line-height: 1.35;
  font-weight: 700;
  color: #1a1a1a;
  margin: 4px 0 0;
  transition: color 0.2s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 22px;
`;

const InsightsIndex = () => {
  const { t, language } = useLandingLanguage();
  const { featured, categories } = usePage().props;
  const copy = t.insights;

  const lead = featured?.[0];
  const rest = (featured || []).slice(1, 5);

  return (
    <Page>
      <SeoHead title={SEO_TITLE[language] || SEO_TITLE.en} />
      <Header forceScrolled />

      <Hero>
        <HeroInner>
          <Kicker>{copy.brand}</Kicker>
          <HeroTitle>{copy.index.title}</HeroTitle>
          <HeroSub>{copy.index.subtitle}</HeroSub>
        </HeroInner>
      </Hero>

      <Container>
        {lead ? (
          <Section>
            <SectionHead>
              <div>
                <SectionTitle>{copy.index.featured}</SectionTitle>
              </div>
            </SectionHead>
            <FeaturedGrid>
              <LeadCard
                href={insightPath(language, lead.slug)}
                $src={lead.cover_url}
              >
                <LeadBody>
                  {lead.category ? (
                    <LeadChip>{lead.category.name}</LeadChip>
                  ) : null}
                  <LeadTitle>{lead.title}</LeadTitle>
                  <LeadExcerpt>{lead.excerpt}</LeadExcerpt>
                </LeadBody>
              </LeadCard>
              <SideList>
                {rest.map((article) => (
                  <SideItem
                    key={article.slug}
                    href={insightPath(language, article.slug)}
                  >
                    <SideThumb $src={article.cover_url} />
                    <SideText>
                      {article.category ? (
                        <SideCat>{article.category.name}</SideCat>
                      ) : null}
                      <SideTitle>{article.title}</SideTitle>
                    </SideText>
                  </SideItem>
                ))}
              </SideList>
            </FeaturedGrid>
          </Section>
        ) : null}

        {(categories || []).map((category) => (
          <Section key={category.slug}>
            <SectionHead>
              <div>
                <SectionTitle>{category.name}</SectionTitle>
                {category.description ? (
                  <SectionDesc>{category.description}</SectionDesc>
                ) : null}
              </div>
              <ViewAll href={categoryPath(language, category.slug)}>
                {copy.index.viewAll} <ArrowRight size={15} />
              </ViewAll>
            </SectionHead>
            <Grid>
              {(category.articles || []).map((article) => (
                <ArticleCard
                  key={article.slug}
                  article={article}
                  showCategory={false}
                />
              ))}
            </Grid>
          </Section>
        ))}
      </Container>

      <Footer />
    </Page>
  );
};

const InsightsIndexPage = () => {
  return (
    <LandingLanguageProvider>
      <InsightsIndex />
    </LandingLanguageProvider>
  );
};

export default InsightsIndexPage;
